/**
 * KpiTile — shared KPI statistic tile (SF-3733).
 *
 * A small presentational wrapper over antd's Statistic with a consistent loading
 * state, em-dash empty fallback, optional trend delta, and theme-driven accent.
 * Reused across Eva pages for headline metrics (spend, users, etc.).
 */
import { Card, Skeleton, Statistic, Tooltip, Typography, theme } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  InfoCircleOutlined,
  MinusOutlined,
} from '@ant-design/icons';
import { EM_DASH } from '../../utils/format';

const { Text } = Typography;

export interface KpiTileProps {
  /** Tile heading. */
  title: string;
  /** Pre-formatted primary value (e.g. "$1,234.56"). */
  value: string;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** Whether the underlying query is loading. */
  loading?: boolean;
  /**
   * Optional signed percentage change vs. a baseline period. Positive renders
   * green-up, negative red-down. null/undefined hides the delta.
   */
  deltaPercent?: number | null;
  /** Caption under the delta (e.g. "vs last month"). */
  deltaCaption?: string;
  /**
   * Whether an increase is a positive signal. For cost metrics an increase is
   * undesirable (default false → up renders red); for user-count metrics an
   * increase is good (true → up renders green).
   */
  higherIsBetter?: boolean;
  /** Optional tooltip on the title (e.g. data-source note). */
  hint?: string;
  /**
   * Optional badge rendered next to the title (e.g. a "Live" indicator when the
   * value is sourced from the live current-month endpoint rather than the
   * nightly rollup — SF-3734/3735). Kept inline with the title so the cue sits
   * with the metric it qualifies.
   */
  badge?: React.ReactNode;
  /**
   * Optional style passthrough to the underlying Card. Used to stretch the tile
   * to a shared row height (e.g. when sitting beside taller editor cards).
   */
  style?: React.CSSProperties;
  /** Optional lines under the value and delta (e.g. a per-day figure and a trend, SF-5117). */
  footer?: React.ReactNode;
}

/** Headline KPI tile with optional period-over-period delta. */
export const KpiTile: React.FC<KpiTileProps> = ({
  title,
  value,
  icon,
  loading = false,
  deltaPercent,
  deltaCaption,
  higherIsBetter = false,
  hint,
  badge,
  style,
  footer,
}) => {
  const { token } = theme.useToken();
  const hasDelta = deltaPercent !== null && deltaPercent !== undefined && Number.isFinite(deltaPercent);
  // Distinguish exactly-zero (flat/unchanged) from a directional move so that a
  // 0% change never renders adverse coloring or a directional arrow.
  const isZero = hasDelta && (deltaPercent as number) === 0;
  const isUp = hasDelta && (deltaPercent as number) > 0;
  // Map direction → sentiment: an increase is "good" only when higherIsBetter.
  // A zero delta is neutral (neither good nor bad).
  const isGood = hasDelta && !isZero && (isUp ? higherIsBetter : !higherIsBetter);

  // When a hint is present, wrap the title in a Tooltip AND show a small (i)
  // info icon as a visible affordance so the tooltip is discoverable. The icon
  // is purely decorative (cursor:help, muted) — the Tooltip carries the text.
  const titleText = hint ? (
    <Tooltip title={hint}>
      {title}
      <InfoCircleOutlined
        aria-hidden
        style={{
          marginInlineStart: 4,
          fontSize: 12,
          color: token.colorTextTertiary,
          cursor: 'help',
        }}
      />
    </Tooltip>
  ) : (
    title
  );
  // Keep the optional badge inline with the title so the "Live" cue qualifies
  // the metric it sits beside without disturbing the Statistic layout.
  const titleNode = badge ? (
    <span>
      {titleText}
      {badge}
    </span>
  ) : (
    titleText
  );

  // Neutral (zero) → muted text + flat indicator; otherwise success/error.
  const deltaColor = isZero
    ? token.colorTextSecondary
    : isGood
      ? token.colorSuccess
      : token.colorError;

  return (
    <Card hoverable style={style}>
      {loading ? (
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '60%' }} />
      ) : (
        <>
          <Statistic
            title={titleNode}
            value={value === '' ? EM_DASH : value}
            prefix={icon}
            valueStyle={{ color: token.colorPrimary }}
          />
          {hasDelta && (
            <Text
              style={{ color: deltaColor, fontSize: 13 }}
            >
              {isZero ? <MinusOutlined /> : isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{' '}
              {Math.abs(deltaPercent as number).toFixed(1)}%
              {deltaCaption ? (
                <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
                  {deltaCaption}
                </Text>
              ) : null}
            </Text>
          )}
          {footer}
        </>
      )}
    </Card>
  );
};
